import { useEffect, useRef, useState } from 'react';
import { Link } from 'react-router-dom';
import { useAuth } from '@/auth/AuthContext';
import { useI18n } from '@/i18n';
import { fetchUpcoming, formatReleaseDate, type UpcomingCard } from '@/lib/upcoming';

export function UpcomingReleases() {
  const { accessToken, sessionId } = useAuth();
  const { t, locale } = useI18n();
  const [cards, setCards] = useState<UpcomingCard[]>([]);
  const scrollerRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    if (!accessToken) return;
    let cancelled = false;

    fetchUpcoming(accessToken, sessionId)
      .then((items) => {
        if (!cancelled) setCards(items.slice(0, 12));
      })
      .catch(() => {
        if (!cancelled) setCards([]);
      });

    return () => {
      cancelled = true;
    };
  }, [accessToken, sessionId, locale]);

  if (cards.length === 0) return null;

  function scroll(direction: -1 | 1) {
    const el = scrollerRef.current;
    if (!el) return;
    el.scrollBy({ left: direction * Math.min(el.clientWidth * 0.72, 360), behavior: 'smooth' });
  }

  return (
    <section className="upcoming" aria-labelledby="upcoming-title">
      <div className="upcoming__inner site-pad-x">
        <header className="upcoming__head">
          <h2 id="upcoming-title" className="section-title upcoming__title">
            {t('upcoming.title')}
          </h2>
          <div className="upcoming__nav">
            <button
              type="button"
              className="upcoming__arrow"
              aria-label={t('upcoming.prev')}
              onClick={() => scroll(-1)}
            >
              <svg viewBox="0 0 16 16" fill="none" aria-hidden="true">
                <path
                  d="M10 3L5 8l5 5"
                  stroke="currentColor"
                  strokeWidth="1.8"
                  strokeLinecap="round"
                  strokeLinejoin="round"
                />
              </svg>
            </button>
            <button
              type="button"
              className="upcoming__arrow"
              aria-label={t('upcoming.next')}
              onClick={() => scroll(1)}
            >
              <svg viewBox="0 0 16 16" fill="none" aria-hidden="true">
                <path
                  d="M6 3l5 5-5 5"
                  stroke="currentColor"
                  strokeWidth="1.8"
                  strokeLinecap="round"
                  strokeLinejoin="round"
                />
              </svg>
            </button>
          </div>
        </header>

        <div className="upcoming__scroller" ref={scrollerRef}>
          {cards.map((card) => (
            <article key={card.id} className="upcoming-card glass-panel-soft">
              <div className="upcoming-card__media">
                <img
                  src={card.art}
                  alt=""
                  onError={(event) => {
                    event.currentTarget.onerror = null;
                    event.currentTarget.src = card.fallback;
                  }}
                />
              </div>
              <div className="upcoming-card__body">
                {card.date ? (
                  <p className="upcoming-card__date">{formatReleaseDate(card.date, locale)}</p>
                ) : null}
                <h3 className="upcoming-card__name">{card.title}</h3>
                <Link to={card.href} className="btn btn-primary upcoming-card__cta">
                  <svg viewBox="0 0 16 16" fill="currentColor" aria-hidden="true">
                    <path d="M5 3.5v9l8-4.5-8-4.5Z" />
                  </svg>
                  {t('upcoming.playDemo')}
                </Link>
              </div>
            </article>
          ))}
        </div>
      </div>
    </section>
  );
}
